/**
 * useClubPermissions Hook
 * Manages permission checking for club management actions
 * Extracted from ClubDetails.js
 */

import { useMemo } from 'react';
import { useUser } from '../../../context/UserContext';

/**
 * Hook to check user permissions for a specific club
 * @param {string} clubId - The club ID to check permissions for
 * @param {Object} clubDetails - Optional club details with currentUserPermissions
 * @returns {Object} Permission flags
 */
const useClubPermissions = (clubId, clubDetails = null) => {
  const { userClubs, userProfile } = useUser();

  /**
   * Check if user can manage programs in this club
   * Considers both explicit permissions and role-based access
   */
  const canManage = useMemo(() => {
    // Check explicit permissions from club details
    const perms = clubDetails?.currentUserPermissions || [];
    const hasExplicit = Array.isArray(perms) && (
      perms.includes('program.manage') || 
      perms.includes('club.manage')
    );

    // Check role-based permissions from user's club memberships
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      const isOwnerOrManager = [
        'owner', 
        'creator', 
        'clubowner', 
        'clubmanager', 
        'manager', 
        'admin'
      ].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    return hasExplicit || hasClubRole;
  }, [clubDetails?.currentUserPermissions, userClubs, clubId]);

  /**
   * Check if user is a club manager (specific roles)
   */
  const isClubManager = useMemo(() => {
    const userClubRole = userClubs?.find(m => {
      const membershipClubId = m.club?._id || m.club;
      return String(membershipClubId) === String(clubId);
    })?.role;
    
    return ['Creator', 'Owner', 'Manager'].includes(userClubRole);
  }, [userClubs, clubId]);

  /**
   * Get user's role in this club
   */
  const userClubRole = useMemo(() => {
    const membership = userClubs?.find(m => {
      const membershipClubId = m.club?._id || m.club;
      return String(membershipClubId) === String(clubId);
    });
    return membership?.role || null;
  }, [userClubs, clubId]);

  /**
   * Check if user is a member of this club
   */
  const isClubMember = useMemo(() => {
    return Array.isArray(userClubs) && userClubs.some(membership => {
      const membershipClubId = membership.club?._id || membership.club || membership.clubId;
      return String(membershipClubId) === String(clubId);
    });
  }, [userClubs, clubId]);

  /**
   * Check if user is the club owner/creator
   */
  const isClubOwner = useMemo(() => {
    const role = userClubRole?.toLowerCase();
    return role === 'owner' || role === 'creator';
  }, [userClubRole]);

  return {
    canManage,
    isClubManager,
    isClubMember,
    isClubOwner,
    userClubRole,
    userProfile
  };
};

export default useClubPermissions;










